import { setHeader, removeHeader, http } from "@/services/http";
import { cloneDeep } from "lodash";
import saveAs from "file-saver";
import Vue from "vue";

/**
 * Holds x number of solutions
 *
 * @type Object
 */

const state = {
  active: 0,
  solutionNumber: 0,
  solutions: [],
};

const getters = {
  solutions: (state) => {
    return state.solutions;
  },
  solution: (state) => {
    return state.solutions[state.active];
  },
  active: (state) => {
    return state.active;
  },
  solutionNumber: (state) => {
    return state.solutionNumber;
  },
  hasSolutions: (state) => {
    return state.solutions.length > 0;
  },
  multiLength: (state) => {
    if (state.solutions[state.active]) {
      return state.solutions[state.active].multiLength;
    }
    return false;
  },
  multiLengthModules: (state) => {
    if (state.solutions[state.active]) {
      return state.solutions[state.active].multiLengthModules;
    }
    return [];
  },
  multiLengthVoltageType: (state) => {
    if (state.solutions[state.active]) {
      return state.solutions[state.active].multiLengthVoltageType;
    }
    return null;
  },
  selectedEmergencyDrivers: (state) => {
    if (state.solutions[state.active]) {
      return state.solutions[state.active].emergencyDrivers;
    }
    return [];
  },
  virtualModule: (state) => {
    if (state.solutions[state.active]) {
      return state.solutions[state.active].virtualModule;
    }
    return null;
  },
};

const mutations = {
  addNewSolution: (state, region) => {
    state.active = addBlankSolution(state, region);
  },
  setModule: (state, module) => {
    state.solutions[state.active].module = module;
  },
  setDriver: (state, driver) => {
    state.solutions[state.active].driver = driver;
  },
  setEmergencyDriver: (state, emergencyDriver) => {
    if (state.solutions[state.active].region === "na") {
      state.solutions[state.active].emergencyDrivers = [];
      state.solutions[state.active].emergencyDrivers.push(emergencyDriver);
    } else {
      const index = state.solutions[state.active].emergencyDrivers.findIndex(
        (x) => x.uid === emergencyDriver.uid
      );
      if (index === -1) {
        state.solutions[state.active].emergencyDrivers.push(emergencyDriver);
      }
    }
  },
  setSensor: (state, sensor) => {
    state.solutions[state.active].sensor = sensor;
  },
  setBridge: (state, bridge) => {
    state.solutions[state.active].bridge = bridge;
  },
  setActiveSensorAccessories: (state, assessories) => {
    state.solutions[state.active].sensor.activeAccessories = assessories;
  },
  setModuleFilters: (state, moduleFilters) => {
    state.solutions[state.active].moduleFilters = moduleFilters;
  },
  setDriverFilters: (state, driverFilters) => {
    state.solutions[state.active].driverFilters = driverFilters;
  },
  setEmergencyDriverFilters: (state, emergencyDriverFilters) => {
    state.solutions[state.active].emergencyDriverFilters =
      emergencyDriverFilters;
  },
  removeModule: (state, index) => {
    state.solutions[index].module = null;
  },
  removeDriver: (state, index) => {
    state.solutions[index].driver = null;
  },
  removeEmergencyDriver: (state, index) => {
    Vue.delete(state.solutions[state.active].emergencyDrivers, index);
  },
  removeSensor: (state, index) => {
    state.solutions[index].sensor = null;
  },
  removeBridge: (state, index) => {
    state.solutions[index].bridge = null;
  },
  removeSolution: (state, index) => {
    state.active = removeSolution(state, index);
  },
  removeActiveSolution: (state) => {
    state.active = removeSolution(state, state.active);
  },
  removeAllSolutions: (state) => {
    state.solutions = [];
  },
  setActiveSolution: (state, index) => {
    state.active = index;
  },
  increaseSolutionNumber: (state) => {
    state.solutionNumber++;
  },
  resetSolutionNumber: (state) => {
    state.solutionNumber = 0;
  },
  setSolutionNumber: (state, solutionNumber) => {
    state.solutionNumber = solutionNumber;
  },
  setInputValues: (state, inputValues) => {
    state.solutions[state.active].inputValues = inputValues;
  },
  setCurrentSolution: (state, solution) => {
    state.solutions[state.active] = solution;
  },
  setFilename: (state, filename) => {
    state.solutions[state.active].filename = filename;
  },
  setUserComment: (state, userComment) => {
    state.solutions[state.active].userComment = userComment;
  },
  setMultiLength: (state, multiLength) => {
    state.solutions[state.active].multiLength = multiLength;
  },
  setMultiLengthModules: (state, multiLengthModules) => {
    state.solutions[state.active].multiLengthModules = multiLengthModules;
  },
  setMultiLengthVoltageType: (state, voltageType) => {
    state.solutions[state.active].multiLengthVoltageType = voltageType;
  },
  setVirtualModule: (state, virtualModule) => {
    // used to calculate multi length system
    state.solutions[state.active].virtualModule = virtualModule;
  },
  setSolutions: (state, solutions) => {
    state.solutions = [...solutions];
  },
  setLor: (state, lor) => {
    const module = cloneDeep(state.solutions[state.active].module);
    if (module.calculationParameters != null) {
      module.calculationParameters.luminaireLor = lor;
    }
    state.solutions[state.active].module = module;
  },
  setGlobalFilters: (state, globalFilters) => {
    state.solutions[state.active].globalFilters = [...globalFilters];
  },
};

const actions = {
  setSolution({ commit, dispatch }, newSolution) {
    // Add new solution
    dispatch("handleNewSolution", true);
    let module = newSolution.module;

    commit("setInputValues", newSolution.inputValues);
    commit("setFilename", newSolution.filename);
    commit("setUserComment", newSolution.userComment);

    if (newSolution.multiLength) {
      commit("setMultiLength", true);
      // Add modules
      commit("setVirtualModule", newSolution.virtualModule);
      commit("setMultiLengthModules", newSolution.multiLengthModules);
      commit("setMultiLengthVoltageType", newSolution.multiLengthVoltageType);
      module = newSolution.virtualModule;
    }

    // Check if solution has a module
    if (module) {
      // Set module from pdf solution data
      commit("setModule", module);
      commit("setModuleFilters", newSolution.moduleFilters);
      commit("brain/drivers/setInputForm", module, {
        root: true,
      });
      commit("brain/modules/setModule", module, {
        root: true,
      });
      commit("brain/modules/setFilters", newSolution.moduleFilters, {
        root: true,
      });
    }

    if (
      newSolution &&
      newSolution.emergencyDrivers &&
      newSolution.emergencyDrivers.length
    ) {
      for (const emergencyDriver of newSolution.emergencyDrivers) {
        commit("setEmergencyDriver", emergencyDriver);
      }
      commit("setEmergencyDriverFilters", newSolution.emergencyDriverFilters);
      commit(
        "brain/emergencyDrivers/setEmergencyDrivers",
        newSolution.emergencyDrivers,
        {
          root: true,
        }
      );
      commit(
        "brain/emergencyDrivers/setFilters",
        newSolution.emergencyDriverFilters,
        {
          root: true,
        }
      );
    }

    if (newSolution.sensor) {
      commit("setSensor", newSolution.sensor);
    }

    if (newSolution.bridge) {
      commit("setBridge", newSolution.bridge);
    }

    if (newSolution.driver) {
      // Set driver from pdf solution data
      commit("setDriver", newSolution.driver);
      commit("setDriverFilters", newSolution.driverFilters);
      commit("brain/drivers/setDriver", newSolution.driver, {
        root: true,
      });
      commit("brain/drivers/setFilters", newSolution.driverFilters, {
        root: true,
      });
    }

    if (newSolution.module) {
      if (newSolution.module.isArchived) {
        commit("brain/global/setArchive", true, { root: true });
      }
    } else if (newSolution.driver.isArchived) {
      commit("brain/global/setArchive", true, { root: true });
    }

    if (newSolution.globalFilters) {
      commit("setGlobalFilters", newSolution.globalFilters);
      commit("brain/global/setFilter", newSolution.globalFilters, {
        root: true,
      });
    }

    dispatch(
      "brain/ui/showToast",
      { type: "success", message: "Uploaded solution successfully" },
      { root: true }
    );
  },

  saveSolution: ({ getters, rootState }, solutionName) => {
    const solutionToUpload = cloneDeep(getters.solution);
    solutionToUpload.filename = solutionName;

    return new Promise((resolve, reject) => {
      http({
        method: "POST",
        url: "/api/v2/users/profile/settings/solutions/save",
        data: {
          name: solutionName,
          solution: solutionToUpload,
          region: rootState.brain.region,
        },
      })
        .then((response) => {
          resolve(response);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },

  /**
   * Add new solution
   * @param commit
   * @param dispatch
   * @param solutionWithProduct boolean if new solution will already contain a product
   */
  handleNewSolution: ({ commit, dispatch, rootState }, solutionWithProduct) => {
    // For every new solution first remove selected product headers
    dispatch("removeProductHeaders");

    if (!solutionWithProduct) {
      // Clear all filters
      dispatch("brain/global/resetAllFilters", null, { root: true });

      // Clear products
      commit("brain/modules/setModule", null, { root: true });
      commit("brain/modules/setIncompatibleModule", null, { root: true });
      commit("brain/drivers/setDriver", null, { root: true });
      commit("brain/emergencyDrivers/setEmergencyDriver", null, { root: true });

      // Clear product calculate performance
      dispatch("brain/modules/clearCalculatedPerformance", null, {
        root: true,
      });
      commit("brain/drivers/clearCalculatedPerformance", null, { root: true });
      commit("brain/emergencyDrivers/clearCalculatedPerformance", null, {
        root: true,
      });
    }

    // Before add increase solution number
    commit("increaseSolutionNumber");

    // Add blank solution
    commit("addNewSolution", rootState.brain.region);
  },

  /**
   * Set headers and active solution
   * @param commit
   * @param dispatch
   * @param getters
   * @param index of solution that will be activated
   */
  handleActiveSolution: ({ commit, dispatch, getters }, index) => {
    return new Promise((resolve) => {
      // First remove selected product headers
      dispatch("removeProductHeaders");

      // Clear all filters
      dispatch("brain/global/resetAllFilters", null, { root: true });

      // Clear product calculate performance
      dispatch("brain/modules/clearCalculatedPerformance", null, {
        root: true,
      });
      commit("brain/drivers/clearCalculatedPerformance", null, { root: true });
      commit("brain/emergencyDrivers/clearCalculatedPerformance", null, {
        root: true,
      });

      commit("brain/modules/setModule", null, { root: true });
      commit("brain/modules/setIncompatibleModule", null, { root: true });
      commit("brain/drivers/setDriver", null, { root: true });
      commit("brain/emergencyDrivers/setEmergencyDriver", null, { root: true });

      // set active solution
      commit("setActiveSolution", index);

      // set product headers of selected solution
      dispatch("setHeaders");

      commit("setInputValues", getters.solution.inputValues);
      commit("setFilename", getters.solution.filename);
      commit("setUserComment", getters.solution.userComment);

      // set products
      if (getters.solution.multiLength) {
        if (getters.solution.multiLengthModules) {
          commit("setModule", getters.solution.virtualModule);
          commit("setModuleFilters", getters.solution.moduleFilters);
          commit("brain/drivers/setInputForm", getters.solution.module, {
            root: true,
          });
          commit("brain/modules/setModule", getters.solution.virtualModule, {
            root: true,
          });
          commit("brain/modules/setFilters", getters.solution.moduleFilters, {
            root: true,
          });

          commit("setInputValues", getters.solution.inputValues);
          commit("setFilename", getters.solution.filename);
          commit("setUserComment", getters.solution.userComment);
          resolve("multiLength");
        }
      } else if (getters.solution.module) {
        commit("setModule", getters.solution.module);
        commit("setModuleFilters", getters.solution.moduleFilters);
        commit("brain/drivers/setInputForm", getters.solution.module, {
          root: true,
        });
        commit("brain/modules/setModule", getters.solution.module, {
          root: true,
        });
        commit("brain/modules/setFilters", getters.solution.moduleFilters, {
          root: true,
        });
      }
      if (getters.solution.driver) {
        commit("setDriver", getters.solution.driver);
        commit("setDriverFilters", getters.solution.driverFilters);
        commit("brain/drivers/setDriver", getters.solution.driver, {
          root: true,
        });
        commit("brain/drivers/setFilters", getters.solution.driverFilters, {
          root: true,
        });
      }
      if (getters.solution.emergencyDriver) {
        commit("setEmergencyDriver", getters.solution.emergencyDriver);
        commit(
          "setEmergencyDriverFilters",
          getters.solution.emergencyDriverFilters
        );
        commit(
          "brain/emergencyDrivers/setEmergencyDriver",
          getters.solution.emergencyDriver,
          {
            root: true,
          }
        );
        commit(
          "brain/emergencyDrivers/setFilters",
          getters.solution.emergencyDriverFilters,
          {
            root: true,
          }
        );
      }

      commit("setGlobalFilters", getters.solution.globalFilters);

      resolve();
    });
  },

  clearActiveSolution({ commit, dispatch, rootState }) {
    dispatch("removeProductHeaders");
    commit("clearActiveSolution", rootState.brain.region);
  },

  /**
   * When removing a selected driver also clear the header
   * @param commit
   * @param dispatch
   * @param index
   */
  removeDriver: ({ commit, dispatch }, index) => {
    commit("removeDriver", index);
    removeHeader("X-Selected-Driver");
    dispatch("checkSolution");
  },

  /**
   * Remove emergency driver
   * @param commit
   * @param dispatch
   * @param index
   */
  removeEmergencyDriver: ({ commit, dispatch }, index) => {
    commit("removeEmergencyDriver", index);
    dispatch("checkSolution");
  },

  /**
   * When removing a selected module also clear the header
   * @param commit
   * @param dispatch
   * @param index
   */
  removeModule: ({ commit, dispatch }, index) => {
    commit("removeModule", index);
    removeHeader("X-Selected-Module");
    dispatch("checkSolution");
  },

  /**
   * Check if solution doesn't have products and can be removed
   * @param getters
   * @param commit
   */
  checkSolution: ({ getters, commit }) => {
    if (getters.solution) {
      if (
        !getters.solution.module &&
        !getters.solution.driver &&
        !getters.solution.emergencyDrivers.length &&
        getters.solutions.length === 1
      ) {
        commit("removeSolution", getters.active);
      }
    }
  },

  /**
   * When a solution is active the module and driver are sent along as header with each request.
   * @param getters
   */
  setHeaders: ({ getters }) => {
    if (getters.solution) {
      if (getters.solution.module) {
        setHeader("Selected-Module", getters.solution.module, true);
      }
      if (getters.solution.driver) {
        setHeader("Selected-Driver", getters.solution.driver, true);
      }
    }
  },

  /**
   * Remove product headers
   */
  removeProductHeaders: () => {
    removeHeader("X-Selected-Driver");
    removeHeader("X-Selected-Module");
  },

  /**
   * Add module or driver to a (new) solution
   *
   * @param commit
   * @param rootState
   * @param dispatch
   * @param getters
   * @param type module | driver
   * @param addNew creates new solution
   * @param data the module or driver data
   */
  add: ({ commit, rootState, dispatch }, { type, addNew, data }) => {
    // First add new blank solution
    if (addNew || state.solutions.length === 0) {
      dispatch("handleNewSolution", true);
    }
    // Then set Module or Driver
    switch (type) {
      case "module":
        commit("setModule", data);
        commit("setModuleFilters", rootState.brain.modules.moduleFilters);
        commit("brain/drivers/setInputForm", data, {
          root: true,
        });
        commit("brain/modules/setModule", data, {
          root: true,
        });
        commit("setGlobalFilters", rootState.brain.global.filters);
        break;
      case "driver":
        commit("setDriver", data);
        commit("setDriverFilters", rootState.brain.drivers.driverFilters);
        commit("brain/drivers/setDriver", data, {
          root: true,
        });
        commit("setGlobalFilters", rootState.brain.global.filters);
        break;
      case "emergency-driver":
        commit(
          "setEmergencyDriver",
          rootState.brain.emergencyDrivers.emergencyDriver
        );
        commit(
          "setEmergencyDriverFilters",
          rootState.brain.emergencyDrivers.emergencyDriverFilters
        );
        break;
    }
  },

  /**
   * Download or print solution
   * @param getters
   * @param commit
   * @param rootGetters
   * @param download if true download instead of print
   */
  download: ({ getters, commit, rootGetters }, download) => {
    commit("brain/global/setBusy", true, { root: true });

    // Check if you have a solution
    if (getters.solution) {
      // Sent solution data to backend to generate PDF, when logged in also sent user data for pdf footer
      http({
        method: "POST",
        url: "/api/v2/solutions/export-pdf",
        data: {
          jwt: rootGetters["brain/auth/activeToken"],
          solution: {
            isWhitelistedSolution:
              rootGetters["brain/drivers/isWhitelistedSolution"],
            ...getters.solution,
          },
          aclFeatures: rootGetters["brain/auth/aclFeatures"],
          user: rootGetters["brain/auth/isLoggedIn"]
            ? rootGetters["brain/auth/userData"]
            : null,
        },
        responseType: "arraybuffer",
        headers: {
          "Content-Type": "application/json",
        },
      })
        .then((res) => {
          // Create blob of received arraybuffer
          const blob = new Blob([res.data], {
            type: "application/pdf",
          });

          // Download or print solution
          if (download) {
            const date = new Date();
            const filename =
              rootGetters["brain/auth/isLoggedIn"] && getters.solution.filename
                ? getters.solution.filename
                : "your-selection-" + date.getTime();

            saveAs(blob, filename + ".pdf");
          } else {
            const pdfUrl = URL.createObjectURL(blob);
            const newWindow = window.open();

            newWindow.onload = () => {
              newWindow.location = pdfUrl;
            };
          }
          commit("brain/global/setBusy", false, { root: true });
        })
        .catch((err) => {
          console.log(err);
          commit("brain/global/setBusy", false, { root: true });
        });
    }
  },

  /**
   * Upload solution and set active products
   * @param commit
   * @param dispatch
   * @param rootState
   * @param solutionFile pdf file containing solution data
   */
  upload: ({ commit, dispatch, rootState }, solutionFile) => {
    commit("brain/global/setBusy", true, { root: true });

    // Send uploaded pdf file to backend to retrieve solution from pdf keywords
    let formData = new FormData();
    formData.append("solution", solutionFile);
    return http({
      method: "POST",
      url: "/api/v2/solutions/upload-pdf",
      data: formData,
      headers: {
        "Content-Type": "multipart/form-data",
      },
    })
      .then(async (res) => {
        // Check if pdf has a solution
        if (res.data.data != null) {
          // Parse solution string to JSON
          const pdfSolution = JSON.parse(res.data.data);
          if (pdfSolution.region === rootState.brain.region) {
            dispatch("setSolution", pdfSolution);
          } else {
            dispatch(
              "brain/ui/showToast",
              {
                type: "error",
                message:
                  "You are uploading a solution from a different region, please switch to " +
                  pdfSolution.region +
                  " before uploading your solution",
              },
              { root: true }
            );
          }
        } else {
          dispatch(
            "brain/ui/showToast",
            {
              type: "error",
              message: "Something went wrong uploading your solution",
            },
            { root: true }
          );
        }
        commit("brain/global/setBusy", false, { root: true });
      })
      .catch((err) => {
        console.log(err);
        dispatch(
          "brain/ui/showToast",
          {
            type: "error",
            message: "Something went wrong uploading your solution",
          },
          { root: true }
        );
        commit("brain/global/setBusy", false, { root: true });
      });
  },

  setSensorFromUrl: ({ commit }, data) => {
    return http({
      method: "GET",
      url: "/api/v2/sensors/find?nc12=" + data.sensorNc12,
      headers: {
        "Content-Type": "multipart/form-data",
      },
    })
      .then((res) => {
        commit("setSensor", res.data.data);
        const mandatoryAccessories = res.data.data.accessories
          .filter((accessory) => accessory.mandatory)
          .map((accessory) => {
            return accessory.id;
          });

        if (data.accessoryIds) {
          const accessoryIds = mandatoryAccessories.concat(
            data.accessoryIds.split(",").map(Number)
          );
          commit("setActiveSensorAccessories", accessoryIds);
        } else {
          commit("setActiveSensorAccessories", mandatoryAccessories);
        }
      })
      .catch((err) => {
        console.log(err);
      });
  },

  setDriverFromUrl: ({ commit }, data) => {
    let formData = new FormData();
    formData.append("driverNc12", data.driverNc12);
    return http({
      method: "POST",
      url: "/api/v2/drivers/data",
      data: formData,
      headers: {
        "Content-Type": "multipart/form-data",
      },
    })
      .then((res) => {
        const driver = res.data.data.hits[0];
        commit("setDriver", driver);
        commit("brain/drivers/setDriver", driver, {
          root: true,
        });
      })
      .catch((err) => {
        console.log(err);
      });
  },

  setBridgeFromUrl: (_, data) => {
    let formData = new FormData();
    formData.append("bridgeNc12", data.bridgeNc12);

    return new Promise((resolve, reject) => {
      http({
        method: "GET",
        url: "/api/v2/bridges/find?nc12=" + data.bridgeNc12,
        data: formData,
        headers: {
          "Content-Type": "multipart/form-data",
        },
      })
        .then((res) => {
          const bridge = res.data.data;
          if (bridge != null) {
            resolve(bridge);
          } else {
            reject("Bridge not found");
          }
        })
        .catch((err) => {
          console.log(err);
          reject(err);
        });
    });
  },

  clearSolutions: ({ commit, dispatch }) => {
    // Clear all filters
    dispatch("brain/global/resetAllFilters", null, { root: true });

    // Remove selected product headers
    dispatch("removeProductHeaders");

    // Clear product calculate performance
    dispatch("brain/modules/clearCalculatedPerformance", null, {
      root: true,
    });
    commit("brain/drivers/clearCalculatedPerformance", null, { root: true });
    commit("brain/emergencyDrivers/clearCalculatedPerformance", null, {
      root: true,
    });

    // Reset solution number
    commit("resetSolutionNumber");

    // Remove all solutions
    commit("removeAllSolutions");
  },

  validateSolution: ({ rootGetters }) => {
    return new Promise((resolve, reject) => {
      let hasMatchingDriver = null;
      let whitelistingEnabled = null;
      let showArchive = null;
      if (rootGetters["brain/global/filters"]) {
        hasMatchingDriver = rootGetters["brain/global/filters"].find(
          (filter) => filter.name === "Has matching driver"
        );
        whitelistingEnabled = rootGetters["brain/global/filters"].find(
          (filter) => filter.name === "Released combinations"
        );
        showArchive = rootGetters["brain/global/filters"].find(
          (filter) => filter.name === "Show archive"
        );
      }

      let flux = null;
      let current = null;

      if (rootGetters["brain/modules/inputLock"] === "flux") {
        flux = rootGetters["brain/modules/moduleFlux"];
      } else if (rootGetters["brain/modules/inputLock"] === "current") {
        if (rootGetters["brain/modules/moduleCurrent"]) {
          current = rootGetters["brain/modules/moduleCurrent"] / 1000;
        }
      }

      http({
        method: "POST",
        url: "/api/v2/solutions/validate-solution",
        data: {
          module: rootGetters["brain/modules/module"],
          driver: rootGetters["brain/drivers/driver"],
          hasMatchingDriver: hasMatchingDriver ? hasMatchingDriver.value : null,
          whitelistingEnabled: whitelistingEnabled
            ? whitelistingEnabled.value
            : null,
          showArchive: showArchive ? showArchive.value : null,
          systemFlux: flux,
          systemCurrent: current,
          systemTemperature: rootGetters["brain/modules/moduleTemperature"],
          luminaireFlux: rootGetters["brain/modules/luminaireFlux"],
          luminaireEfficacy: null,
          luminaireLor: rootGetters["brain/modules/lor"],
          driverCurrent: rootGetters["brain/drivers/driverCurrent"],
          driverVoltage: rootGetters["brain/drivers/driverVoltage"],
          driverPower: rootGetters["brain/drivers/driverPower"],
          // Todo make dynamic when RTH is implemented
          tAmbient: 25,
          rth: null,
        },
        headers: {
          "Content-Type": "application/json",
        },
      })
        .then((res) => {
          resolve(res.data.data);
        })
        .catch((err) => {
          reject(err.response.data.message);
        });
    });
  },
  setMultiLengthModules: (
    { commit, dispatch },
    { virtualModule, multiLengthModules, voltageType }
  ) => {
    // Add new solution
    dispatch("handleNewSolution", false);
    commit("setMultiLength", true);
    commit("brain/modules/setMultiLength", true, { root: true });

    // Add modules
    commit("setVirtualModule", virtualModule);
    commit("setMultiLengthModules", multiLengthModules);
    // Persist the chosen voltage type so recalculations can disambiguate HV/LV modules
    commit("setMultiLengthVoltageType", voltageType);
  },
};

/**
 * Adds a blank solution to the state
 * @param state
 * @param region
 */
function addBlankSolution(state, region) {
  state.solutions = [
    ...state.solutions,
    {
      module: null,
      moduleFilters: null,
      driver: null,
      driverFilters: null,
      emergencyDrivers: [],
      emergencyDriverFilters: null,
      sensor: null,
      bridge: null,
      inputValues: null,
      filename: null,
      userComment: null,
      solutionNumber: state.solutionNumber,
      region: region,
      multiLength: false,
      multiLengthModules: [],
      multiLengthVoltageType: null,
      virtualModule: null,
      globalFilters: [],
    },
  ];
  return state.solutions.length - 1;
}

function removeSolution(state, index) {
  // Get solutionNumber of active solution
  const activeSolutionNumber = state.solutions[state.active].solutionNumber;

  // Remove solution at requested index
  state.solutions.splice(index, 1);

  // If removed solution was not active, return active solution index
  if (index !== state.active) {
    return state.solutions.findIndex(
      (solution) => solution.solutionNumber === activeSolutionNumber
    );
  }

  // If no more solutions after removal, also remove product headers
  if (state.solutions.length === 0) {
    removeHeader("X-Selected-Driver");
    removeHeader("X-Selected-Module");
    state.solutionNumber = 0;
  }

  // Return new solution index
  const newIndex = state.solutions.length - 1;
  return newIndex > 0 ? newIndex : 0;
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
